import { NavLink, Outlet } from "react-router-dom";
import { Settings as SettingsIcon, ShieldCheck } from "lucide-react";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";

type SettingsTabKey =
  | "users"
  | "company"
  | "integrations"
  | "templates"
  | "products"
  | "accountingSystems"
  | "opportunityTags"
  | "automations"
  | "perks"
  | "prompts"
  | "tracking"
  | "logs";

type SettingsTab = {
  to: string;
  label: string;
  key: SettingsTabKey;
  end?: boolean;
};

const SETTINGS_TABS: SettingsTab[] = [
  { to: "/settings/users", label: "Users", key: "users" },
  { to: "/settings/company", label: "Company", key: "company" },
  { to: "/settings/integrations", label: "Integrations", key: "integrations" },
  { to: "/settings/templates", label: "Templates", key: "templates" },
  { to: "/settings/products", label: "Products", key: "products" },
  { to: "/settings/accounting-systems", label: "Accounting systems", key: "accountingSystems" },
  { to: "/settings/opportunity-tags", label: "Opportunity tags", key: "opportunityTags" },
  { to: "/settings/automations", label: "Automations", key: "automations" },
  { to: "/settings/perks", label: "Perks", key: "perks" },
  { to: "/settings/prompts", label: "Prompts", key: "prompts" },
  { to: "/settings/tracking", label: "Tracking", key: "tracking" },
  { to: "/settings/logs", label: "Logs", key: "logs" },
];

export function SettingsLayout() {
  const me = trpc.auth.me.useQuery();
  const isAdmin = me.data?.role === "admin";
  const enabled = !!me.data && isAdmin;

  const usersQuery = trpc.users.listUsers.useQuery(undefined, { enabled });
  const allowedEmailsQuery = trpc.users.listAllowedEmails.useQuery(undefined, { enabled });
  const contractTemplatesQuery = trpc.contractTemplates.list.useQuery(undefined, { enabled });
  const productsQuery = trpc.products.listWithPrices.useQuery(undefined, { enabled });
  const accountingSystemsQuery = trpc.accountingSystems.list.useQuery(undefined, { enabled });
  const opportunityTagsQuery = trpc.opportunityTags.list.useQuery(undefined, { enabled });
  const automationRulesQuery = trpc.automationRules.list.useQuery(undefined, { enabled });
  const perkCatalogQuery = trpc.perkCatalog.list.useQuery(undefined, { enabled });
  const promptsQuery = trpc.prompts.list.useQuery(undefined, { enabled });
  const emailTemplatesQuery = trpc.emailTemplates.list.useQuery(undefined, { enabled });

  const usersCount = (() => {
    const users = usersQuery.data;
    const allowed = allowedEmailsQuery.data;
    if (!users || !allowed) return undefined;
    const emails = new Set<string>();
    for (const u of users) emails.add(u.email.toLowerCase());
    for (const a of allowed) emails.add(a.email.toLowerCase());
    return emails.size;
  })();

  const templatesCount = (() => {
    const contractCount = contractTemplatesQuery.data?.length;
    const emailCount = emailTemplatesQuery.data?.length;
    if (contractCount === undefined && emailCount === undefined) return undefined;
    return (contractCount ?? 0) + (emailCount ?? 0);
  })();

  const counts: Record<SettingsTabKey, number | undefined> = {
    users: usersCount,
    company: undefined,
    integrations: undefined,
    templates: templatesCount,
    products: productsQuery.data?.length,
    accountingSystems: accountingSystemsQuery.data?.length,
    opportunityTags: opportunityTagsQuery.data?.length,
    automations: automationRulesQuery.data?.length,
    perks: perkCatalogQuery.data?.length,
    prompts: promptsQuery.data?.length,
    tracking: undefined,
    logs: undefined,
  };

  if (me.isLoading) {
    return <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>;
  }

  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-12 md:px-6">
        <div className="rounded-lg border border-dashed p-8 text-center">
          <ShieldCheck className="mx-auto mb-3 h-8 w-8 text-muted-foreground" />
          <h2 className="text-lg font-semibold">Admin access required</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            This page is only available to administrators.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="border-b">
        <div className="mx-auto w-full max-w-screen-2xl px-4 pt-6 md:px-6">
          <div className="mb-4 flex items-center gap-3">
            <SettingsIcon className="h-6 w-6 text-muted-foreground" />
            <div>
              <h1 className="text-2xl font-semibold tracking-tight">Settings</h1>
              <p className="text-sm text-muted-foreground">
                Manage users, integrations and content used across Noium Quotes.
              </p>
            </div>
          </div>
          <nav
            aria-label="Settings sections"
            className="-mx-px overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
          >
            <div className="flex w-max items-center gap-1 py-2">
              {SETTINGS_TABS.map((tab) => {
                const count = counts[tab.key];
                return (
                  <NavLink
                    key={tab.to}
                    to={tab.to}
                    end={tab.end}
                    className={({ isActive }) =>
                      cn(
                        "flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border px-3 py-1.5 text-sm font-medium transition-colors",
                        isActive
                          ? "border-transparent bg-accent text-accent-foreground"
                          : "border-border bg-background text-muted-foreground hover:bg-accent/50 hover:text-foreground"
                      )
                    }
                  >
                    <span>{tab.label}</span>
                    {count ? (
                      <span className="inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-muted px-1.5 text-xs font-medium tabular-nums text-muted-foreground">
                        {count}
                      </span>
                    ) : null}
                  </NavLink>
                );
              })}
            </div>
          </nav>
        </div>
      </div>
      <Outlet />
    </div>
  );
}
